/* eslint-disable */
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Loader2, Save, FileDown, Mail, CheckCircle2,
  Wallet, ReceiptText, Ban, FileText, RefreshCw,
} from "lucide-react";

const KIND_TITLE = { factura: "Factură fiscală", proforma: "Factură proformă", chitanta: "Chitanță" };
const PAY_METHODS = [
  { id: "transfer", label: "Transfer bancar" }, { id: "cash", label: "Numerar" },
  { id: "card", label: "Card" }, { id: "op", label: "Ordin de plată" }, { id: "ramburs", label: "Ramburs" },
];
const STATUS = {
  draft: { label: "Ciornă", cls: "bg-[#1E293B] text-[#94A3B8]" },
  issued: { label: "Emisă", cls: "bg-[#0C2A45] text-[#38BDF8]" },
  paid: { label: "Plătită", cls: "bg-[#0C3320] text-[#4ADE80]" },
  cancelled: { label: "Anulată", cls: "bg-[#1E293B] text-[#64748B]" },
};

const emptyClient = { name: "", cui: "", reg_com: "", address: "", city: "", county: "", country: "România", email: "", phone: "", is_company: true };
const emptyItem = () => ({ description: "", quantity: 1, unit: "buc", unit_price: 0, vat_rate: 19 });

export default function InvoiceEditor() {
  const { id, kind: kindParam } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [kind, setKind] = useState(kindParam || "factura");
  const [inv, setInv] = useState(null);
  const [client, setClient] = useState(emptyClient);
  const [items, setItems] = useState([emptyItem()]);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("transfer");
  const [notes, setNotes] = useState("");
  const [recAmount, setRecAmount] = useState(0);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState("");
  const [payModal, setPayModal] = useState(false);

  const money = (v) => `${Number(v || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`;

  const load = async () => {
    try {
      const r = await api.get(`/invoices/${id}`);
      const d = r.data;
      setInv(d); setKind(d.kind);
      setClient({ ...emptyClient, ...(d.client || {}) });
      setItems((d.items || []).length ? d.items.map((it) => ({ ...emptyItem(), ...it })) : [emptyItem()]);
      setIssueDate(d.issue_date || ""); setDueDate(d.due_date || "");
      setPaymentMethod(d.payment_method || "transfer"); setNotes(d.notes || "");
      setRecAmount(d.totals?.total || 0);
    } catch { toast.error("Document inexistent"); nav("/app/facturare"); } finally { setLoading(false); }
  };
  useEffect(() => { if (!isNew) load(); }, [id]);

  const totals = useMemo(() => {
    let subtotal = 0, vat = 0;
    items.forEach((it) => {
      const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      subtotal += line; vat += line * (Number(it.vat_rate) || 0) / 100;
    });
    return { subtotal: +subtotal.toFixed(2), vat: +vat.toFixed(2), total: +(subtotal + vat).toFixed(2) };
  }, [items]);

  const isChitanta = kind === "chitanta";
  const readOnly = inv?.status === "cancelled";
  const issued = !!inv?.full_number;

  const buildBody = () => {
    let outItems = items;
    if (isChitanta) outItems = [{ description: notes || "Contravaloare", quantity: 1, unit: "buc", unit_price: Number(recAmount) || 0, vat_rate: 0 }];
    return {
      kind, client, items: outItems.map((it) => ({
        description: it.description, quantity: Number(it.quantity) || 0, unit: it.unit || "buc",
        unit_price: Number(it.unit_price) || 0, vat_rate: Number(it.vat_rate) || 0,
      })),
      issue_date: issueDate || null, due_date: dueDate || null,
      payment_method: paymentMethod, notes,
    };
  };

  const save = async ({ silent } = {}) => {
    setBusy("save");
    try {
      let res;
      if (isNew && !inv) res = await api.post("/invoices", buildBody());
      else res = await api.put(`/invoices/${inv.id}`, buildBody());
      setInv(res.data);
      if (!silent) toast.success("Salvat");
      if (isNew && res.data?.id) nav(`/app/facturare/${res.data.id}`, { replace: true });
      return res.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la salvare"); return null; }
    finally { setBusy(""); }
  };

  const issue = async () => {
    const saved = inv ? inv : await save({ silent: true });
    if (!saved) return;
    setBusy("issue");
    try { const r = await api.post(`/invoices/${saved.id}/issue`); setInv(r.data); toast.success(`Emisă: ${r.data.full_number}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare la emitere"); } finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    const saved = inv || (await save({ silent: true }));
    if (!saved) return;
    setBusy("pdf");
    try {
      const token = localStorage.getItem("offerai_token");
      const r = await api.get(`/invoices/${saved.id}/pdf?auth=${token}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${(saved.full_number || saved.kind)}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Eroare PDF"); } finally { setBusy(""); }
  };

  const addPayment = async (amount, method) => {
    setBusy("pay");
    try { const r = await api.post(`/invoices/${inv.id}/payments`, { amount, method }); setInv(r.data); toast.success("Plată înregistrată"); setPayModal(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); } finally { setBusy(""); }
  };

  const makeReceipt = async () => {
    setBusy("receipt");
    try { const r = await api.post(`/invoices/${inv.id}/receipt`, { method: "cash" }); toast.success(`Chitanță emisă: ${r.data.full_number}`); nav(`/app/facturare/${r.data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); } finally { setBusy(""); }
  };

  const convert = async () => {
    setBusy("convert");
    try { const r = await api.post(`/invoices/${inv.id}/convert`); toast.success(`Factură creată: ${r.data.full_number}`); nav(`/app/facturare/${r.data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); } finally { setBusy(""); }
  };

  const cancelInv = async () => {
    if (!window.confirm("Anulezi acest document? Acțiunea nu poate fi refăcută.")) return;
    setBusy("cancel");
    try { const r = await api.post(`/invoices/${inv.id}/cancel`); setInv(r.data); toast.success("Anulat"); }
    catch (e) { toast.error("Eroare"); } finally { setBusy(""); }
  };

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, emptyItem()]);
  const delItem = (i) => setItems((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#00BFFF]" /></div>;

  const st = STATUS[inv?.status] || STATUS.draft;

  return (
    <div className="p-5 lg:p-8 max-w-4xl mx-auto pb-28" data-testid="invoice-editor">
      <button onClick={() => nav("/app/facturare")} className="text-sm text-[#94A3B8] hover:text-[#E2E8F0] flex items-center gap-1.5 mb-4" data-testid="back-btn"><ArrowLeft size={16} /> Înapoi la facturare</button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#00BFFF]">{KIND_TITLE[kind]}</div>
          <h1 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            {inv?.full_number || "Document nou"}
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
          </h1>
        </div>
      </div>

      {/* Client */}
      <div className="card-flat p-5 mt-6">
        <div className="font-semibold text-sm mb-3 flex items-center gap-2"><FileText size={15} className="text-[#00BFFF]" /> Date client</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nume / Denumire" value={client.name} onChange={(v) => setClient({ ...client, name: v })} testid="client-name" />
          <Field label="CUI / CNP" value={client.cui} onChange={(v) => setClient({ ...client, cui: v })} testid="client-cui" />
          <Field label="Nr. Reg. Comerțului" value={client.reg_com} onChange={(v) => setClient({ ...client, reg_com: v })} testid="client-regcom" />
          <Field label="Email" value={client.email} onChange={(v) => setClient({ ...client, email: v })} testid="client-email" />
          <Field label="Adresă" value={client.address} onChange={(v) => setClient({ ...client, address: v })} testid="client-address" />
          <Field label="Localitate / Județ" value={client.city} onChange={(v) => setClient({ ...client, city: v })} testid="client-city" />
        </div>
      </div>

      {/* Items or amount */}
      {isChitanta ? (
        <div className="card-flat p-5 mt-4">
          <div className="font-semibold text-sm mb-3">Chitanță</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#94A3B8]">Sumă primită (RON)</label>
              <input type="number" value={recAmount} onChange={(e) => setRecAmount(e.target.value)} className="input-flat w-full mt-1" data-testid="receipt-amount" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8]">Modalitate</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="input-flat w-full mt-1">
                {PAY_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs text-[#94A3B8]">Reprezentând</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="input-flat w-full mt-1" placeholder="Contravaloare factura ..." data-testid="receipt-notes" />
          </div>
        </div>
      ) : (
        <div className="card-flat p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-sm">Articole</div>
            <button onClick={addItem} className="btn-ink text-xs flex items-center gap-1.5" data-testid="add-item-btn"><Plus size={14} /> Adaugă rând</button>
          </div>
          <div className="space-y-2">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] text-[#94A3B8] px-1">
              <div className="col-span-5">Descriere</div><div className="col-span-1">Cant.</div><div className="col-span-1">UM</div>
              <div className="col-span-2">Preț unit.</div><div className="col-span-1">TVA%</div><div className="col-span-2 text-right">Valoare</div>
            </div>
            {items.map((it, i) => {
              const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-row-${i}`}>
                  <input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Denumire produs/serviciu" className="input-flat col-span-12 sm:col-span-5" data-testid={`item-desc-${i}`} />
                  <input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="input-flat col-span-3 sm:col-span-1" data-testid={`item-qty-${i}`} />
                  <input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="input-flat col-span-3 sm:col-span-1" />
                  <input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="input-flat col-span-3 sm:col-span-2" data-testid={`item-price-${i}`} />
                  <input type="number" value={it.vat_rate} onChange={(e) => setItem(i, "vat_rate", e.target.value)} className="input-flat col-span-2 sm:col-span-1" data-testid={`item-vat-${i}`} />
                  <div className="col-span-10 sm:col-span-2 text-right text-sm font-semibold">{money(line)}</div>
                  <button onClick={() => delItem(i)} className="col-span-2 sm:col-span-1 text-[#EF4444] flex justify-end" data-testid={`del-item-${i}`}><Trash2 size={16} /></button>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end mt-4">
            <div className="w-full sm:w-64 space-y-1.5 text-sm">
              <div className="flex justify-between text-[#94A3B8]"><span>Total fără TVA</span><span>{money(totals.subtotal)}</span></div>
              <div className="flex justify-between text-[#94A3B8]"><span>Total TVA</span><span>{money(totals.vat)}</span></div>
              <div className="flex justify-between font-display font-extrabold text-base pt-1.5 border-t border-[#1E293B]"><span>Total de plată</span><span className="text-[#00BFFF]" data-testid="grand-total">{money(totals.total)}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Meta */}
      {!isChitanta && (
        <div className="card-flat p-5 mt-4 grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#94A3B8]">Data emiterii</label>
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="input-flat w-full mt-1" data-testid="issue-date" />
          </div>
          <div>
            <label className="text-xs text-[#94A3B8]">Scadență</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="input-flat w-full mt-1" data-testid="due-date" />
          </div>
          <div>
            <label className="text-xs text-[#94A3B8]">Modalitate de plată</label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="input-flat w-full mt-1">
              {PAY_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3">
            <label className="text-xs text-[#94A3B8]">Mențiuni</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input-flat w-full mt-1" />
          </div>
        </div>
      )}

      {/* Payments list */}
      {inv?.payments?.length > 0 && (
        <div className="card-flat p-5 mt-4">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Wallet size={15} className="text-[#4ADE80]" /> Plăți înregistrate</div>
          {inv.payments.map((p) => (
            <div key={p.id} className="flex justify-between text-sm py-1.5 border-b border-[#1E293B] last:border-0">
              <span className="text-[#94A3B8]">{p.date} · {PAY_METHODS.find((m) => m.id === p.method)?.label || p.method}{p.note ? ` · ${p.note}` : ""}</span>
              <span className="font-semibold">{money(p.amount)}</span>
            </div>
          ))}
          {inv.balance > 0 && <div className="flex justify-between text-sm mt-2 text-[#FBBF24] font-semibold"><span>Rest de plată</span><span>{money(inv.balance)}</span></div>}
        </div>
      )}

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-[#0B111D]/95 backdrop-blur border-t border-[#1E293B] p-3 z-40">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-2 justify-end">
          {!readOnly && <button onClick={() => save()} disabled={busy === "save"} className="btn-ink flex items-center gap-2" data-testid="save-invoice-btn">{busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salvează</button>}
          {!issued && !readOnly && <button onClick={issue} disabled={busy === "issue"} className="btn-primary flex items-center gap-2" data-testid="issue-invoice-btn">{busy === "issue" ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Emite</button>}
          {issued && <button onClick={downloadPdf} disabled={busy === "pdf"} className="btn-ink flex items-center gap-2" data-testid="pdf-invoice-btn">{busy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} PDF</button>}
          {issued && !isChitanta && inv?.balance > 0 && inv?.status !== "cancelled" && <button onClick={() => { setRecAmount(inv.balance); setPayModal(true); }} className="btn-ink flex items-center gap-2" data-testid="add-payment-btn"><Wallet size={15} /> Plată</button>}
          {issued && !isChitanta && inv?.status !== "cancelled" && <button onClick={makeReceipt} disabled={busy === "receipt"} className="btn-ink flex items-center gap-2" data-testid="make-receipt-btn">{busy === "receipt" ? <Loader2 size={15} className="animate-spin" /> : <ReceiptText size={15} />} Chitanță</button>}
          {kind === "proforma" && issued && !inv?.converted_to && <button onClick={convert} disabled={busy === "convert"} className="btn-ink flex items-center gap-2" data-testid="convert-btn">{busy === "convert" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} → Factură</button>}
          {issued && inv?.status !== "cancelled" && <button onClick={cancelInv} disabled={busy === "cancel"} className="btn-ink text-[#EF4444] flex items-center gap-2" data-testid="cancel-invoice-btn"><Ban size={15} /> Anulează</button>}
        </div>
      </div>

      {payModal && <PayModal defaultAmount={recAmount} busy={busy === "pay"} onClose={() => setPayModal(false)} onSubmit={addPayment} />}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-[#94A3B8]">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-flat w-full mt-1" data-testid={testid} />
    </div>
  );
}

function PayModal({ defaultAmount, busy, onClose, onSubmit }) {
  const [amount, setAmount] = useState(defaultAmount || 0);
  const [method, setMethod] = useState("transfer");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="payment-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-sm rounded-2xl p-5">
        <div className="font-display font-bold text-lg mb-3">Înregistrează plată</div>
        <label className="text-xs text-[#94A3B8]">Sumă (RON)</label>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-flat w-full mt-1" data-testid="payment-amount" />
        <label className="text-xs text-[#94A3B8] mt-3 block">Modalitate</label>
        <select value={method} onChange={(e) => setMethod(e.target.value)} className="input-flat w-full mt-1">
          {PAY_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ink">Anulează</button>
          <button onClick={() => onSubmit(Number(amount), method)} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="payment-submit">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Wallet size={15} />} Salvează
          </button>
        </div>
      </div>
    </div>
  );
}
